import { getAuthToken } from '../../lib/api';
import {
  ChatRequest,
  ChatResponse,
  ConversationDetail,
  ConversationSummary,
  Evidence,
  HealthResponse,
  KeyFact,
  ResponseTable,
  SessionInfo,
  StoredMessage,
} from './types';

// The chatbot router is mounted at `/chatbot/*` directly under the backend host
// (per ChatbotIntegration.md §2), NOT under `/api`. Strip a trailing `/api` from
// the shared env var so existing config keeps working without a second variable.
function resolveChatbotBaseUrl(): string {
  const raw =
    (import.meta.env.VITE_CHATBOT_BASE_URL as string | undefined) ||
    (import.meta.env.VITE_API_BASE_URL as string | undefined) ||
    'http://localhost:8000';
  return raw.replace(/\/api\/?$/, '').replace(/\/$/, '');
}

const BASE = resolveChatbotBaseUrl();

// Build request headers, attaching the same `Authorization: Bearer <jwt>` the
// rest of the app uses (token lives in localStorage under `auth_token`; see
// lib/api.ts). The chatbot uses its own `fetch` wrapper rather than the shared
// axios client, so the header is added here explicitly.
//
// Auth is OPTIONAL on /chatbot/chat — an unauthenticated turn still answers but
// the backend won't persist it to history. It is REQUIRED on the
// /chatbot/conversations/* endpoints (401 without a token).
function buildHeaders(json = true): HeadersInit {
  const headers: Record<string, string> = {};
  if (json) headers['Content-Type'] = 'application/json';
  const token = getAuthToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

export class ChatbotApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details: Record<string, any> = {}
  ) {
    super(message);
    this.name = 'ChatbotApiError';
  }
}

async function parseErrorPayload(res: Response): Promise<ChatbotApiError> {
  let detail: any = {};
  try {
    const body = await res.json();
    detail = body?.detail ?? body ?? {};
  } catch {
    // ignore — fall through to status text
  }
  const code =
    typeof detail === 'object' && detail?.code ? String(detail.code) : 'unknown';
  const message =
    typeof detail === 'object' && detail?.message
      ? String(detail.message)
      : typeof detail === 'string'
        ? detail
        : res.statusText || 'Request failed';
  return new ChatbotApiError(res.status, code, message, detail);
}

export async function postChat(
  body: ChatRequest,
  signal?: AbortSignal
): Promise<ChatResponse> {
  const res = await fetch(`${BASE}/chatbot/chat`, {
    method: 'POST',
    headers: buildHeaders(),
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) {
    throw await parseErrorPayload(res);
  }
  return res.json();
}

export async function resetSession(session_id: string): Promise<void> {
  const res = await fetch(`${BASE}/chatbot/session/reset`, {
    method: 'POST',
    headers: buildHeaders(),
    body: JSON.stringify({ session_id }),
  });
  if (!res.ok) {
    throw await parseErrorPayload(res);
  }
}

export async function getHealth(): Promise<HealthResponse> {
  const res = await fetch(`${BASE}/chatbot/health`);
  if (!res.ok) {
    throw await parseErrorPayload(res);
  }
  return res.json();
}

export async function getSession(session_id: string): Promise<SessionInfo> {
  const res = await fetch(
    `${BASE}/chatbot/session/${encodeURIComponent(session_id)}`
  );
  if (!res.ok) {
    throw await parseErrorPayload(res);
  }
  return res.json();
}

// ───────────────────────────────────────────────────────────────────────────
// Conversation history (authenticated)
//
// "New Chat" needs NO backend call — the frontend mints a fresh session_id and
// starts posting to /chatbot/chat; the row is created and auto-titled on the
// first message. These endpoints manage the persisted history list.
// ───────────────────────────────────────────────────────────────────────────

/** First defined value among the given keys (handles camelCase/snake_case). */
function pick<T = any>(obj: Record<string, any>, ...keys: string[]): T | undefined {
  for (const k of keys) {
    if (obj && obj[k] !== undefined && obj[k] !== null) return obj[k] as T;
  }
  return undefined;
}

const EMPTY_EVIDENCE: Evidence = {
  nodes: [],
  relationships: [],
  derivation: '',
  cypher: null,
};

/** Normalize one evidence relationship to the snake_case shape the UI expects. */
function normalizeRelationship(r: Record<string, any>) {
  return {
    id: String(pick(r, 'id', 'element_id', 'elementId') ?? ''),
    type: String(pick(r, 'type') ?? ''),
    start_node_id: String(pick(r, 'start_node_id', 'startNodeId') ?? ''),
    end_node_id: String(pick(r, 'end_node_id', 'endNodeId') ?? ''),
    properties: pick<Record<string, any>>(r, 'properties') ?? {},
  };
}

/** Normalize an evidence blob (camelCase or snake_case) into the UI's Evidence. */
function normalizeEvidence(raw: any): Evidence {
  if (!raw || typeof raw !== 'object') return { ...EMPTY_EVIDENCE };
  const nodes = (pick<any[]>(raw, 'nodes') ?? []).map((n) => ({
    id: String(pick(n, 'id', 'element_id', 'elementId') ?? ''),
    labels: pick<string[]>(n, 'labels') ?? [],
    properties: pick<Record<string, any>>(n, 'properties') ?? {},
  }));
  const relationships = (pick<any[]>(raw, 'relationships') ?? []).map(
    normalizeRelationship
  );
  return {
    nodes,
    relationships,
    derivation: String(pick(raw, 'derivation') ?? ''),
    cypher: pick<string>(raw, 'cypher') ?? null,
    cypher_parameterized:
      pick<string>(raw, 'cypher_parameterized', 'cypherParameterized') ?? null,
    parameters: pick<Record<string, any>>(raw, 'parameters') ?? undefined,
  };
}

/**
 * Rebuild a live-chat ChatResponse from a stored assistant message so the
 * existing ChatMessage component can render it unchanged. The rich payload may
 * be flat on the message or nested under `metadata` — we merge both and read
 * defensively (camelCase or snake_case).
 */
function storedToChatResponse(m: StoredMessage, sessionId: string): ChatResponse {
  const meta = (m.metadata && typeof m.metadata === 'object' ? m.metadata : {}) as Record<
    string,
    any
  >;
  const src: Record<string, any> = { ...meta, ...m };
  const evidence = normalizeEvidence(pick(src, 'evidence'));
  // Stored assistant turns keep the runnable Cypher as a flat field on the
  // message (per ChatbotIntegration.md), not nested under `evidence`. When the
  // evidence blob carries no query, fall back to that flat field so a reopened
  // conversation still surfaces the "View evidence (Cypher)" affordance.
  if (!evidence.cypher) {
    evidence.cypher = pick<string>(src, 'cypher', 'query') ?? null;
  }
  return {
    session_id: sessionId,
    summary: String(pick(src, 'summary') ?? ''),
    answer: String(pick(src, 'answer', 'content', 'text') ?? ''),
    key_facts: (pick<KeyFact[]>(src, 'key_facts', 'keyFacts') ?? []).map((f: any) => ({
      label: String(pick(f, 'label') ?? ''),
      value: String(pick(f, 'value') ?? ''),
      source_node_id: pick<string>(f, 'source_node_id', 'sourceNodeId') ?? null,
    })),
    table: (pick<ResponseTable>(src, 'table') ?? null) as ResponseTable | null,
    evidence,
    confidence: (pick(src, 'confidence') ?? 'medium') as ChatResponse['confidence'],
    context_used: String(pick(src, 'context_used', 'contextUsed') ?? ''),
    suggested_questions:
      pick<string[]>(src, 'suggested_questions', 'suggestedQuestions') ?? [],
    follow_up: pick<string>(src, 'follow_up', 'followUp') ?? null,
    clarification_required:
      pick<boolean>(src, 'clarification_required', 'clarificationRequired') ?? false,
    options: pick<string[]>(src, 'options') ?? [],
  };
}

/** Map a stored transcript message to the UI message shape used by the page. */
export function storedMessageToUi(
  m: StoredMessage,
  sessionId: string,
  index: number
): import('./types').ChatbotUiMessage {
  const id = `stored_${sessionId}_${index}`;
  if (m.role === 'user') {
    return { kind: 'user', id, text: String(pick(m, 'content', 'text') ?? '') };
  }
  return { kind: 'assistant', id, data: storedToChatResponse(m, sessionId) };
}

/** Normalize one conversation summary row (camelCase or snake_case). */
function normalizeSummary(raw: Record<string, any>): ConversationSummary {
  return {
    sessionId: String(pick(raw, 'sessionId', 'session_id', 'id') ?? ''),
    title: String(pick(raw, 'title') ?? 'Untitled conversation'),
    createdAt: String(pick(raw, 'createdAt', 'created_at') ?? ''),
    updatedAt: String(
      pick(raw, 'updatedAt', 'updated_at', 'createdAt', 'created_at') ?? ''
    ),
    messageCount: pick<number>(raw, 'messageCount', 'message_count', 'turnCount', 'turn_count'),
    preview: pick<string>(raw, 'preview', 'lastMessage', 'last_message') ?? null,
  };
}

/** GET /chatbot/conversations — history sidebar list (auth required). */
export async function listConversations(
  signal?: AbortSignal
): Promise<ConversationSummary[]> {
  const res = await fetch(`${BASE}/chatbot/conversations`, {
    method: 'GET',
    headers: buildHeaders(false),
    signal,
  });
  if (!res.ok) throw await parseErrorPayload(res);
  const body = await res.json();
  // Tolerate { conversations: [...] }, { items: [...] }, or a bare array.
  const rows: any[] = Array.isArray(body)
    ? body
    : (body?.conversations ?? body?.items ?? []);
  return rows.map(normalizeSummary);
}

/** GET /chatbot/conversations/{session_id} — full transcript (auth required). */
export async function getConversation(
  session_id: string,
  signal?: AbortSignal
): Promise<ConversationDetail> {
  const res = await fetch(
    `${BASE}/chatbot/conversations/${encodeURIComponent(session_id)}`,
    { method: 'GET', headers: buildHeaders(false), signal }
  );
  if (!res.ok) throw await parseErrorPayload(res);
  const body = await res.json();
  return {
    sessionId: String(pick(body, 'sessionId', 'session_id', 'id') ?? session_id),
    title: String(pick(body, 'title') ?? 'Untitled conversation'),
    createdAt: String(pick(body, 'createdAt', 'created_at') ?? ''),
    updatedAt: String(pick(body, 'updatedAt', 'updated_at') ?? ''),
    messages: (pick<StoredMessage[]>(body, 'messages', 'turns') ?? []) as StoredMessage[],
  };
}

/** PATCH /chatbot/conversations/{session_id} — rename (auth required). */
export async function renameConversation(
  session_id: string,
  title: string
): Promise<void> {
  const res = await fetch(
    `${BASE}/chatbot/conversations/${encodeURIComponent(session_id)}`,
    {
      method: 'PATCH',
      headers: buildHeaders(),
      body: JSON.stringify({ title }),
    }
  );
  if (!res.ok) throw await parseErrorPayload(res);
}

/** DELETE /chatbot/conversations/{session_id} — soft-delete (auth required). */
export async function deleteConversation(session_id: string): Promise<void> {
  const res = await fetch(
    `${BASE}/chatbot/conversations/${encodeURIComponent(session_id)}`,
    { method: 'DELETE', headers: buildHeaders(false) }
  );
  if (!res.ok) throw await parseErrorPayload(res);
}

export const chatbotApi = {
  postChat,
  resetSession,
  getHealth,
  getSession,
  listConversations,
  getConversation,
  renameConversation,
  deleteConversation,
  storedMessageToUi,
  baseUrl: BASE,
};
